import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import AppShell from "../components/AppShell";
import {
  downloadSetupPackage,
  getInstallerInfo,
  listPendingSessions,
  type InstallerInfo,
  type PendingSession,
} from "../api/agents";
import { useAuth } from "../stores/auth";

const PENDING_POLL_MS = 5_000;

export default function AddComputer() {
  const [installer, setInstaller] = useState<InstallerInfo | null>(null);
  const [installerReady, setInstallerReady] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  async function onDownloadPackage() {
    setDownloading(true);
    setDownloadError(null);
    try {
      await downloadSetupPackage();
    } catch (e) {
      setDownloadError(downloadErrorMessage(e));
    } finally {
      setDownloading(false);
    }
  }
  const [pending, setPending] = useState<PendingSession[]>([]);
  const [codeCopied, setCodeCopied] = useState(false);
  const tenant = useAuth((s) => s.tenant);
  // Extract once so TS narrows it to `string` for the JSX below — the
  // optional-chain narrowing doesn't always propagate through JSX
  // children, and the field is optional now (see auth.ts).
  const officeCode = tenant?.office_code;

  useEffect(() => {
    getInstallerInfo()
      .then((info) => {
        setInstaller(info);
        setInstallerReady(true);
      })
      .catch(() => {
        setInstallerReady(true); // not available — show coming-soon notice
      });
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function poll() {
      try {
        const data = await listPendingSessions();
        if (!cancelled) setPending(data);
      } catch {
        // silent — keep last good list
      }
    }
    poll();
    const id = setInterval(poll, PENDING_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  return (
    <AppShell>
      <div className="mx-auto max-w-2xl">
        <Link to="/computers" className="text-sm text-slate-600 hover:underline">
          ← Back to computers
        </Link>
        <h1 className="mt-2 text-2xl font-semibold text-slate-900">Add a computer</h1>
        <p className="mt-2 text-slate-600">
          Run the installer on the computer you want to add. A browser window will open here
          for you to approve it.
        </p>

        {pending.length > 0 && (
          <div className="mt-8 rounded-2xl border border-blue-200 bg-blue-50 p-5">
            <div className="flex items-center gap-3">
              <div className="h-2 w-2 animate-pulse rounded-full bg-blue-600" />
              <p className="text-sm font-semibold text-blue-900">
                {pending.length === 1
                  ? "1 computer is waiting for your approval"
                  : `${pending.length} computers are waiting for your approval`}
              </p>
            </div>
            <ul className="mt-4 space-y-2">
              {pending.map((s) => {
                // /approve flow needs a 4-digit OOB from the PC's screen
                // — different page than the legacy /connect confirm.
                const href =
                  s.kind === "pc_enrollment"
                    ? `/approve/${s.code}`
                    : `/connect/${s.code}`;
                return (
                  <li
                    key={`${s.kind}:${s.code}`}
                    className="flex items-center justify-between rounded-lg bg-white p-3 shadow-sm"
                  >
                    <div>
                      <p className="font-medium text-slate-900">{s.hostname}</p>
                      <p className="text-xs text-slate-500">
                        Started {new Date(s.created_at).toLocaleTimeString()}
                        {s.kind === "pc_enrollment" && (
                          <> · needs 4-digit code from the PC's screen</>
                        )}
                      </p>
                    </div>
                    <Link
                      to={href}
                      className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
                    >
                      Connect this computer
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {officeCode && (
          <div className="mt-8 rounded-2xl border-2 border-slate-200 bg-gradient-to-br from-slate-50 to-white p-6">
            <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
              Your office code
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <p className="font-mono text-3xl font-bold tracking-widest text-slate-900">
                {officeCode}
              </p>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(officeCode);
                  setCodeCopied(true);
                  window.setTimeout(() => setCodeCopied(false), 1500);
                }}
                className="no-tap-highlight rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
              >
                {codeCopied ? "Copied!" : "Copy"}
              </button>
            </div>
            <p className="mt-3 text-sm text-slate-600">
              Share this with anyone setting up a new computer. They open{" "}
              <span className="font-mono text-slate-900">{window.location.host}/join</span> on the
              new PC, enter this code, and we'll email you a link to approve from
              wherever you are. You'll still confirm a 4-digit code they read off
              their screen.
            </p>
          </div>
        )}

        <ol className="mt-8 space-y-6">
          <li className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="text-sm font-medium text-slate-500">Step 1</div>
            <h2 className="mt-1 text-lg font-semibold text-slate-900">
              Download and run the installer
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              Download the installer below, copy it to the computer you want to add, and
              double-click it. It runs automatically — no typing required.
            </p>

            <div className="mt-4">
              {!installerReady ? (
                <div className="h-10 w-48 animate-pulse rounded-lg bg-slate-100" />
              ) : installer?.package && !installer.can_download ? (
                <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-700">
                  Ask the office owner (or an admin) to download the setup program from this page. It contains the
                  office&rsquo;s joining key, so only they can download it.
                </div>
              ) : installer?.package ? (
                <div>
                  <button
                    type="button"
                    onClick={() => void onDownloadPackage()}
                    disabled={downloading}
                    className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-medium text-white [-webkit-tap-highlight-color:transparent] hover:bg-slate-700 disabled:opacity-60"
                  >
                    {downloading ? "Preparing the download…" : "Download KraetdNet-Setup.exe"}
                  </button>
                  {downloadError ? (
                    <p role="alert" className="mt-2 text-sm text-red-700">
                      {downloadError}
                    </p>
                  ) : null}
                  <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm text-slate-600">
                    <li>
                      Get KraetdNet-Setup.exe onto the computer you want to add: copy it with a USB drive, or sign in to
                      this dashboard on that computer and download it there.
                    </li>
                    <li>
                      Double-click it. If Windows says &ldquo;unknown publisher&rdquo;, click{" "}
                      <span className="font-medium">More info</span>, then <span className="font-medium">Run anyway</span>.
                    </li>
                    <li>That&rsquo;s it: the computer joins the office by itself. Nothing to type.</li>
                  </ol>
                  <p className="mt-2 text-xs text-slate-500">
                    This file can join computers to your office. Keep it inside the office and delete it from USB
                    drives when you&rsquo;re done.
                  </p>
                </div>
              ) : installer ? (
                <a
                  href={installer.download_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-slate-700"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
                  </svg>
                  Download installer
                  {installer.version && (
                    <span className="rounded bg-slate-700 px-1.5 py-0.5 text-xs font-normal">
                      v{installer.version}
                    </span>
                  )}
                </a>
              ) : (
                <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
                  The download link isn't available yet — KraetdTech will provide the
                  installer file during onboarding.
                </div>
              )}
            </div>
          </li>

          <li className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="text-sm font-medium text-slate-500">Step 2</div>
            <h2 className="mt-1 text-lg font-semibold text-slate-900">
              Approve it in this browser
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              The installer opens a browser window on that computer pointing here. Log in to
              KraetdNet and click <strong>Connect this computer</strong>. That's it.
            </p>
          </li>

          <li className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="text-sm font-medium text-slate-500">Step 3</div>
            <h2 className="mt-1 text-lg font-semibold text-slate-900">It shows up here</h2>
            <p className="mt-2 text-sm text-slate-600">
              Once approved, the computer appears on your{" "}
              <Link to="/computers" className="font-medium text-slate-900 underline">
                computers list
              </Link>{" "}
              within a minute and its shared folder becomes available.
            </p>
          </li>
        </ol>
      </div>
    </AppShell>
  );
}

function downloadErrorMessage(e: unknown): string {
  if (e instanceof Error && e.message.startsWith("Only")) return e.message;
  const response = (e as { response?: { status?: number } })?.response;
  if (!response) return "Couldn't reach the office server. Check this computer's connection and try again.";
  return "The download didn't start. Please try again.";
}
